import { EmbeddedMessage, MessageMedium } from 'constants/messageMediums';
import { match } from 'ts-pattern';
import { isAborted, isArchived, isFinished, isRunning } from 'utils/campaigns';
import { stripPrefix } from 'utils/stringHelpers';
import { CampaignAlert } from '../campaigns/alerts/types';
import {
  AlertFilterType,
  CAMPAIGN_FILTER_OPTIONS,
  CampaignFilterId,
  CampaignFilterPrefix,
  PRE_LAUNCH_CAMPAIGN_STATES,
} from './constants';
import { Campaign, CampaignStateDefinitions, CampaignTypeDefinitions } from './domainTypes';
import { FilterOption, Filters } from './types';
import { MetaApprovalStatusDefinition, PseudoApprovalStatusDefinition } from 'modules/graphql';

export const wasArchived = (campaign: Campaign): boolean =>
  campaign.campaignState === CampaignStateDefinitions.Archived;

export const isNotYetRunning = (campaign: Campaign) =>
  PRE_LAUNCH_CAMPAIGN_STATES.includes(campaign.campaignState);

export const getCampaignLink = (campaign: Campaign): string => {
  // business logic for workflow triggered campaigns: https://iterable.atlassian.net/browse/APP-3855?focusedCommentId=94153

  if (isNotYetRunning(campaign)) {
    return `/templates/edit?templateId=${campaign.templateId}&campaignId=${campaign.id}`;
  }

  if (campaign.campaignState === CampaignStateDefinitions.Scheduled) {
    return `/campaigns/review?campaignId=${campaign.id}`;
  }

  // (Aborted, Archived, Finished, Recurring, Running) *GroupRecurringCampaigns are handled separately
  return `/analytics/campaignPerformance?campaignId=${campaign.id}`;
};

// Return array if it exists and has a length greater than 0, otherwise return null
export function checkArrayLength<T = any>(array?: T[]): T[] | null {
  return array && array.length > 0 ? array : null;
}

// queryLabel and queryId, are the values from the query to set as the label and id for the menu option
// for locales it would be something like queryLabel: languageName and queryId: tag
export const getFilterOptions = (
  isEmbeddedPlacementsOn = false,
  isCampaignAlertsOn = false,
  isWhatsAppOn = false,
) => {
  return CAMPAIGN_FILTER_OPTIONS.filter((opt) => {
    return match(opt)
      .with({ id: CampaignFilterId.Placements }, () => isEmbeddedPlacementsOn)
      .with({ id: CampaignFilterId.Alerts }, () => isCampaignAlertsOn)
      .with({ id: CampaignFilterId.TemplateType }, () => isWhatsAppOn)
      .with({ id: CampaignFilterId.TemplateStatus }, () => isWhatsAppOn)
      .with({ id: CampaignFilterId.TemplateQualityRating }, () => isWhatsAppOn)
      .otherwise(() => true);
  });
};

// Flattens the selected filters from a 2D array
export const flattenFilters = (
  filters: Filters[],
  isEmbeddedPlacementsOn: boolean,
  isCampaignAlertsOn: boolean,
  isWhatsAppOn: boolean,
): string[] => {
  const filterOptions = getFilterOptions(isEmbeddedPlacementsOn, isCampaignAlertsOn, isWhatsAppOn);

  return filters
    .map((filterOption: Filters, idx) => {
      const option = filterOptions[idx];

      return filterOption.map((str) => `${option?.prefix}${str}`);
    })
    .flat();
};

export const truncateText = (text: string, characterLimit: number): string => {
  return text.length > characterLimit ? `${text.slice(0, characterLimit)}...` : text;
};

/** Decimal converted and rounded to the nearest whole percentage */
export const parseDeliveryMetricPercentage = (metric: number): string =>
  `${Math.round(metric * 100)}%`;

/** Decimal converted to a percentage with 2 decimal place precision */
export const parseEngagementMetricPercentage = (metric: number): string =>
  `${(metric * 100).toFixed(2)}%`;

export const shouldDisplayCampaignMetrics = (campaign: Campaign) =>
  isRunning(campaign) || isFinished(campaign) || isArchived(campaign) || isAborted(campaign);

export const getIsEmbeddedMessage = (messageMedium: MessageMedium) =>
  messageMedium === EmbeddedMessage;

/* returns boolean value if project has alerts */
export const hasProjectAlerts = (
  campaignType: CampaignTypeDefinitions,
  projectCampaignAlerts: CampaignAlert[],
) =>
  projectCampaignAlerts?.filter((alert) => campaignType?.includes(alert.campaignType))?.length > 0;

export const getAlertsFilterInfo = (alertFilters: string[]) => {
  const campaignAlertTriggerWindow = alertFilters.includes(AlertFilterType.ANY_ALERT_TRIGGERED)
    ? 'P30D'
    : alertFilters.includes(AlertFilterType.NEW_ALERT_TRIGGERED)
      ? 'PT24H'
      : null;

  const campaignAlertsEnabled = alertFilters.includes(AlertFilterType.ALERTS_ENABLED) || null;

  return {
    campaignAlertTriggerWindow,
    campaignAlertsEnabled,
  };
};

// Strip prefixes to save local storage
// parses to number if isNumber attribute in constants
export const getPrefixToSelectedFiltersMap = (
  filters: string[][],
  isEmbeddedMessagingEnabled: boolean,
  isCampaignAlertsOn: boolean,
  isWhatsAppOn: boolean,
) => {
  const filterOptions = getFilterOptions(
    isEmbeddedMessagingEnabled,
    isCampaignAlertsOn,
    isWhatsAppOn,
  );

  return filters.reduce(
    (acc, filterOption, idx) => {
      const filter = filterOptions[idx];
      const { prefix } = filter;

      const selectedFilterValues = filterOption.map((str: string): FilterOption => {
        const filterString = stripPrefix(prefix, str);
        return filter.isNumber ? parseInt(filterString, 10) : filterString;
      });

      return { ...acc, [prefix as CampaignFilterPrefix]: selectedFilterValues };
    },
    {} as Record<CampaignFilterPrefix, Filters>,
  );
};

export const replacePseudoStatus = (statuses: string[] | null): string[] | null => {
  if (!statuses?.length) return null;

  return statuses.flatMap((status) =>
    status === PseudoApprovalStatusDefinition.Paused
      ? [MetaApprovalStatusDefinition.FirstPaused, MetaApprovalStatusDefinition.SecondPaused]
      : [status],
  );
};
